import type { JSX } from 'react';
import { useStore } from '@/state/store';
import { Panel, Row } from '@/components/ui/Panel';
import { assignmentColor, ASSIGNMENT_LABEL } from '@/origami/types';
import { toDegrees } from '@/constants/numeric';

/**
 * Sector-angle inspector: the teaching surface for Kawasaki and Maekawa.
 *
 * The diagram draws the vertex's creases at their true headings, alternately
 * shading the sectors so the two alternating groups Kawasaki compares are
 * visible at a glance, and marks the sectors that carry the residual.
 */
export function VertexInspector(): JSX.Element | null {
  const vertex = useStore((s) => s.inspectedVertex);
  const analysis = useStore((s) => s.analysis);
  const pattern = useStore((s) => s.pattern);
  const inspectEdge = useStore((s) => s.inspectEdge);
  const theme = useStore((s) => s.view.theme);

  if (vertex === null || vertex >= analysis.vertices.length) return null;
  const topo = analysis.topology.vertices[vertex];
  const { kawasaki, maekawa } = analysis.vertices[vertex];
  const coords = pattern.verticesCoords[vertex];

  return (
    <Panel title={`Vertex ${vertex}`}>
      <Row label="Position" value={`${coords.x.toFixed(5)}, ${coords.y.toFixed(5)}`} />
      <Row label="Degree" value={topo.degree} />
      <Row
        label="Kind"
        value={topo.interior ? 'interior' : (topo.boundaryReason ?? 'boundary')}
        tone={topo.interior ? 'ok' : 'dim'}
      />

      {topo.degree > 0 ? <SectorDiagram vertex={vertex} /> : null}

      <div style={{ marginTop: 6 }}>
        {topo.sectors.map((s, i) => (
          <Row
            key={i}
            label={
              <span>
                <span
                  className="swatch"
                  style={{
                    background: i % 2 === 0 ? 'var(--accent)' : 'var(--text-faint)',
                    marginRight: 6,
                  }}
                />
                α{i + 1}
              </span>
            }
            value={`${toDegrees(s).toFixed(3)}°`}
            tone={kawasaki.worstSectors.includes(i) ? 'warn' : undefined}
          />
        ))}
      </div>

      <div style={{ marginTop: 8 }}>
        <div className="row">
          <span className="k">Creases</span>
          <span className="v" />
        </div>
        {topo.edges.map((e) => (
          <button
            key={e}
            className="issue info"
            style={{ borderLeftColor: assignmentColor(pattern.edgesAssignment[e], theme) }}
            onClick={() => inspectEdge(e)}
          >
            <span className="t">
              edge {e} · {ASSIGNMENT_LABEL[pattern.edgesAssignment[e]]}
            </span>
          </button>
        ))}
      </div>

      <h4 style={{ margin: '12px 0 2px', fontSize: 11.5 }}>Kawasaki</h4>
      {kawasaki.applicable ? (
        <>
          <Row
            label="Status"
            value={kawasaki.satisfied ? 'Locally flat-foldable' : 'Violated'}
            tone={kawasaki.satisfied ? 'ok' : 'warn'}
          />
          <Row label="Residual" value={`${Math.abs(kawasaki.residualDegrees).toFixed(6)}°`} />
          <Row label="Odd sectors" value={`${toDegrees(kawasaki.oddSum).toFixed(4)}°`} />
          <Row label="Even sectors" value={`${toDegrees(kawasaki.evenSum).toFixed(4)}°`} />
          <p className="note">
            α₁ + α₃ + … and α₂ + α₄ + … must each equal 180°. This is necessary and sufficient for a
            single vertex to fold flat; it says nothing about the pattern as a whole.
          </p>
        </>
      ) : (
        <p className="note">
          Not applicable: {reasonText(kawasaki.notApplicableReason)}
        </p>
      )}

      <h4 style={{ margin: '12px 0 2px', fontSize: 11.5 }}>Maekawa</h4>
      <Row label="Mountain" value={maekawa.mountains} />
      <Row label="Valley" value={maekawa.valleys} />
      {maekawa.flats > 0 ? <Row label="Flat" value={maekawa.flats} tone="dim" /> : null}
      {maekawa.unassigned > 0 ? (
        <Row label="Unassigned" value={maekawa.unassigned} tone="dim" />
      ) : null}
      {maekawa.applicable ? (
        <>
          <Row
            label="|M − V|"
            value={Math.abs(maekawa.difference)}
            tone={maekawa.satisfied ? 'ok' : 'warn'}
          />
          <p className="note">
            {maekawa.satisfied
              ? 'Maekawa satisfied: |M − V| = 2, as every flat-folded interior vertex must be.'
              : 'A flat-folded interior vertex always has |M − V| = 2, so this vertex cannot fold flat.'}
          </p>
        </>
      ) : (
        <p className="note">Not applicable: {reasonText(maekawa.notApplicableReason)}</p>
      )}
    </Panel>
  );
}

function reasonText(reason?: string): string {
  switch (reason) {
    case 'boundary-vertex':
      return 'this vertex sits on the edge of the paper, where the theorems say nothing.';
    case 'odd-degree':
      return 'the vertex has odd degree, and a flat-foldable interior vertex must have even degree.';
    case 'degree-too-low':
      return 'a flat-foldable interior vertex needs at least four creases.';
    case 'unassigned-creases':
      return 'some creases here have no mountain/valley assignment yet.';
    default:
      return 'the preconditions are not met.';
  }
}

function SectorDiagram({ vertex }: { vertex: number }): JSX.Element {
  const analysis = useStore((s) => s.analysis);
  const pattern = useStore((s) => s.pattern);
  const theme = useStore((s) => s.view.theme);
  const topo = analysis.topology.vertices[vertex];
  const { kawasaki } = analysis.vertices[vertex];

  const size = 190;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 26;
  const centre = pattern.verticesCoords[vertex];

  const headings = topo.neighbours.map((n) => {
    const p = pattern.verticesCoords[n];
    return Math.atan2(p.y - centre.y, p.x - centre.x);
  });

  // SVG y is down, so negate to keep the diagram matching the editor.
  const point = (angle: number, radius: number): [number, number] => [
    cx + Math.cos(angle) * radius,
    cy - Math.sin(angle) * radius,
  ];

  const wedges: JSX.Element[] = [];
  for (let i = 0; i < headings.length; i++) {
    const a0 = headings[i];
    const sweep = topo.sectors[i];
    if (!Number.isFinite(sweep)) continue;
    const a1 = a0 + sweep;
    const [x0, y0] = point(a0, r);
    const [x1, y1] = point(a1, r);
    const large = sweep > Math.PI ? 1 : 0;
    const isWorst = kawasaki.worstSectors.includes(i);
    wedges.push(
      <path
        key={`w${i}`}
        d={`M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 0 ${x1} ${y1} Z`}
        fill={
          isWorst
            ? 'rgba(224, 169, 27, 0.30)'
            : i % 2 === 0
              ? 'rgba(76, 141, 255, 0.20)'
              : 'rgba(150, 160, 176, 0.12)'
        }
        stroke="none"
      />,
    );
    const [lx, ly] = point(a0 + sweep / 2, r * 0.63);
    wedges.push(
      <text
        key={`t${i}`}
        x={lx}
        y={ly}
        fontSize="9.5"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="var(--text-dim)"
        fontFamily="var(--font-mono)"
      >
        {toDegrees(sweep).toFixed(1)}
      </text>,
    );
  }

  return (
    <svg className="sector-diagram" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" />
      {wedges}
      {headings.map((a, i) => {
        const [x, y] = point(a, r);
        const edge = topo.edges[i];
        return (
          <g key={`e${i}`}>
            <line
              x1={cx}
              y1={cy}
              x2={x}
              y2={y}
              stroke={assignmentColor(pattern.edgesAssignment[edge], theme)}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <text
              x={cx + Math.cos(a) * (r + 12)}
              y={cy - Math.sin(a) * (r + 12)}
              fontSize="9"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="var(--text-faint)"
              fontFamily="var(--font-mono)"
            >
              {pattern.edgesAssignment[edge]}
            </text>
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r={3} fill="var(--text)" />
    </svg>
  );
}
